import { useSelector } from 'react-redux';
import React from 'react';
import {
  useStateName,
  useStateField,
  useStateCode,
} from '~state/getStateField';
import formatAge from '~utils/formatAge';
import PropTypes from 'prop-types';
import { registrationEligibilityDaysFrom18 } from '~components/Register/specialCases';
import { NextElectionDateForCopy } from '../../utils/displayOptions';

function PreregistrationEligibleStatement({
  ineligibleState,
  introductoryPhrase,
}) {
  const stateName = useStateName();
  const stateCode = useStateCode();
  const minimumAgeRegister = useStateField('minimum_age_register');
  const minimumAgeFormatted = formatAge(minimumAgeRegister);
  const eligibilityDaysFrom18 =
    stateCode in registrationEligibilityDaysFrom18
      ? registrationEligibilityDaysFrom18[stateCode]
      : null;

  return (
    <p>
      {introductoryPhrase} {stateName} offers a pre-registration program
      {minimumAgeRegister && (
        <>
          {' '}
          for voters{' '}
          {eligibilityDaysFrom18 ? (
            <span className='font-bold'>
              who will turn 18 sometime in the next {eligibilityDaysFrom18} days
            </span>
          ) : (
            <>
              <span className='font-bold'>{minimumAgeFormatted} </span> and
              older
            </>
          )}
        </>
      )}
      . That means{' '}
      {ineligibleState ? (
        <span>you are old enough to pre-register to vote</span>
      ) : (
        <span>you can pre-register to vote today</span>
      )}
      , and your registration will be automatically activated on your 18th
      birthday.{' '}
    </p>
  );
}

PreregistrationEligibleStatement.propTypes = {
  ineligibleState: PropTypes.bool,
  introductoryPhrase: PropTypes.string,
};

PreregistrationEligibleStatement.defaultProps = {
  ineligibleState: false,
};

function ElectionDayEligibleStatement({ ineligibleState, introductoryPhrase }) {
  const introductoryPhraseIsFragment = introductoryPhrase
    ? introductoryPhrase.slice(-1).match(/[a-z]/i)
    : null;

  return (
    <p>
      {introductoryPhrase}{' '}
      {introductoryPhraseIsFragment ? <>you&apos;ll</> : <>You&apos;ll</>} be 18
      on or before{' '}
      <span className='font-bold'>
        {NextElectionDateForCopy('the next general election')}
      </span>
      , so{' '}
      {ineligibleState ? (
        <>you are old enough to register to vote.</>
      ) : (
        <>you can go ahead and register to vote.</>
      )}
    </p>
  );
}

ElectionDayEligibleStatement.propTypes = {
  ineligibleState: PropTypes.bool,
  introductoryPhrase: PropTypes.string,
};

ElectionDayEligibleStatement.defaultProps = {
  ineligibleState: false,
};

function NextStepsStatement({ ineligibleState }) {
  if (ineligibleState) {
    return <p>However, please make note of these special instructions:</p>;
  }

  return <p>How would you like to register to vote?</p>;
}

NextStepsStatement.propTypes = {
  ineligibleState: PropTypes.bool,
};

export function FuturevoterEligibleStatement({
  ineligibleState,
  displayGoodNews,
}) {
  const futurevoterFields = useSelector((state) => state.futurevoterFields);
  let introductoryPhrase = '';
  if (displayGoodNews) {
    introductoryPhrase = 'Good news!';
  } else if (ineligibleState) {
    introductoryPhrase = 'The good news is that';
  }

  if (
    futurevoterFields.preregistration_available &&
    futurevoterFields.meets_age_minimum
  ) {
    return (
      <PreregistrationEligibleStatement
        ineligibleState={ineligibleState}
        introductoryPhrase={introductoryPhrase}
      />
    );
  }

  if (
    futurevoterFields.will_be_18_by_next_election &&
    futurevoterFields.meets_age_minimum
  ) {
    return (
      <ElectionDayEligibleStatement
        ineligibleState={ineligibleState}
        introductoryPhrase={introductoryPhrase}
      />
    );
  }
}

FuturevoterEligibleStatement.propTypes = {
  ineligibleState: PropTypes.bool,
  displayGoodNews: PropTypes.bool,
};

FuturevoterEligibleStatement.defaultProps = {
  ineligibleState: false,
  displayGoodNews: false,
};

export default function FuturevoterEligibleContent({ ineligibleState }) {
  const futurevoterFields = useSelector((state) => state.futurevoterFields);

  if (futurevoterFields.age < 18) {
    return (
      <>
        <FuturevoterEligibleStatement ineligibleState={ineligibleState} />
        <NextStepsStatement />
      </>
    );
  }

  return null;
}

FuturevoterEligibleContent.propTypes = {
  ineligibleState: PropTypes.bool,
};

FuturevoterEligibleContent.defaultProps = {
  ineligibleState: false,
};

export function FuturevoterEligibleHeader({ displayGoodNews }) {
  const futurevoterFields = useSelector((state) => state.futurevoterFields);
  const stateName = useStateName();
  const isPreregistration =
    futurevoterFields.preregistration_available &&
    futurevoterFields.meets_age_minimum;

  return (
    <h1>
      {displayGoodNews && <>Good news. </>}You are old enough to{' '}
      {isPreregistration ? <>pre-register</> : <>register</>} in {stateName}
    </h1>
  );
}

FuturevoterEligibleHeader.propTypes = {
  displayGoodNews: PropTypes.bool,
};

FuturevoterEligibleHeader.defaultProps = {
  displayGoodNews: false,
};
